import { type PayloadAction, createSlice } from "@reduxjs/toolkit"

import { LOCAL_STATES } from "../../../constants/states.ts"
import {
  afterFailedFetch,
  afterFetch,
  beforeFetch
} from "../../../redux/shared/paged-list"
import { SCROLL_PAGE_SIZE } from "../../../redux/shared/paged-list.ts"
import { getResponseMessage } from "../../../utils/handlers/get-response-message"
import { logout } from "../../LoginPage/redux/auth.thunks"
import { RESULT_TYPES } from "../constants/constants"
import { subcontractorsSliceName } from "./subcontractors.constants.ts"
import {
  activateSubcontractor,
  archiveSubcontractor,
  createInviteSubcontractor,
  createSubcontractor,
  deleteSubcontractor,
  fetchSubcontractorDetails,
  fetchSubcontractors,
  fetchSubcontractorsFilterOptions,
  inviteNoAccessSubcontractor,
  rateSubcontractor,
  resendInviteSubcontractor,
  restoreSubcontractor,
  suspendSubcontractor,
  updateSubcontractor
} from "./subcontractors.thunks"
import type {
  SubcontractorsFilters,
  SubcontractorsState
} from "./subcontractors.types.ts"

const initialState: SubcontractorsState = {
  fetching: false,
  requestId: null,
  result: {
    count: 0,
    next: null,
    results: []
  },

  filters: {
    page: 1,
    ordering: null,
    page_size: SCROLL_PAGE_SIZE,
    q: null,
    specialization: null,
    status: null,
    compliance: null,
    archived: null,
    price_range: null,
    rating: null
  },

  fetchingFilterOptions: false,
  filterOptions: {
    specialization: [],
    status: [],
    price_range: [],
    rating_range: [],
    location: []
  },

  saveFetching: false,
  saveResult: null,
  newSubcontractor: null,

  subcontractorDetailsFetching: false,
  subcontractorDetails: null
}

const subcontractorsSlice = createSlice({
  name: subcontractorsSliceName,
  initialState,
  reducers: {
    subcontractorsSetProp: {
      reducer: (
        state,
        action: PayloadAction<{
          key: keyof SubcontractorsState
          value: SubcontractorsState[keyof SubcontractorsState]
        }>
      ) => {
        const { key, value } = action.payload

        if (value && typeof value === "object" && !Array.isArray(value)) {
          state[key] = {
            ...(state[key] as Record<string, unknown>),
            ...value
          } as SubcontractorsState[keyof SubcontractorsState]
        } else {
          state[key] = value
        }
      },
      prepare: (
        key: keyof SubcontractorsState,
        value: SubcontractorsState[keyof SubcontractorsState]
      ) => ({ payload: { key, value } })
    },

    subcontractorsSetFilters: (
      state,
      action: PayloadAction<Partial<SubcontractorsFilters>>
    ) => {
      state.filters = { ...state.filters, ...action.payload }
    },

    subcontractorsClearFilters: (state) => {
      state.filters = initialState.filters
    }
  },
  extraReducers: (builder) => {
    builder
      // fetchSubcontractorsFilterOptions
      .addCase(fetchSubcontractorsFilterOptions.pending, (state) => {
        state.fetchingFilterOptions = true
      })
      .addCase(fetchSubcontractorsFilterOptions.fulfilled, (state, action) => {
        state.fetchingFilterOptions = false
        state.filterOptions = {
          ...state.filterOptions,
          ...action.payload,
          location: action?.payload?.["location"] || LOCAL_STATES
        }
      })
      .addCase(fetchSubcontractorsFilterOptions.rejected, (state) => {
        state.fetchingFilterOptions = false
      })

      // fetchSubcontractors
      .addCase(fetchSubcontractors.pending, (state, action) => {
        state.fetching = true
        state.requestId = action.meta.requestId
        state.result = beforeFetch(
          state.result,
          initialState.result,
          action.meta?.arg?.isMore
        )
      })
      .addCase(fetchSubcontractors.fulfilled, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.fetching = false
        state.requestId = null
        state.result = afterFetch(
          state.result,
          action.payload,
          action.meta?.arg?.isMore
        )
      })
      .addCase(fetchSubcontractors.rejected, (state, action) => {
        if (state.requestId !== action.meta.requestId) return
        state.fetching = false
        state.requestId = null
        state.result = afterFailedFetch(
          state.result,
          initialState.result,
          action.meta?.arg?.isMore
        )
      })

      // createInviteSubcontractor
      .addCase(createInviteSubcontractor.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(createInviteSubcontractor.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = {
          type: RESULT_TYPES.INVITE,
          message: getResponseMessage(action.payload) ?? undefined,
          data: action.payload?.data
        }
      })
      .addCase(createInviteSubcontractor.rejected, (state) => {
        state.saveFetching = false
      })

      // createSubcontractor
      .addCase(createSubcontractor.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(createSubcontractor.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = {
          data: action.payload?.data,
          type: RESULT_TYPES.CREATE,
          message: getResponseMessage(
            action.payload,
            "Subcontractor created successfully."
          )
        }
      })
      .addCase(createSubcontractor.rejected, (state) => {
        state.saveFetching = false
      })

      // updateSubcontractor
      .addCase(updateSubcontractor.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(updateSubcontractor.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = {
          type: RESULT_TYPES.UPDATE,
          message: getResponseMessage(action.payload) ?? undefined
        }
      })
      .addCase(updateSubcontractor.rejected, (state) => {
        state.saveFetching = false
      })

      // deleteSubcontractor
      .addCase(deleteSubcontractor.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(deleteSubcontractor.fulfilled, (state, action) => {
        const newResults = state.result.results.filter(
          (subcontractor) => subcontractor.id !== action.payload?.id
        )

        state.saveFetching = false
        state.saveResult = {
          type: RESULT_TYPES.DELETE,
          message: getResponseMessage(
            action.payload,
            "Subcontractor deleted successfully"
          )
        }
        state.result = {
          ...state.result,
          count: state.result.count - 1,
          results: newResults
        }
      })
      .addCase(deleteSubcontractor.rejected, (state) => {
        state.saveFetching = false
      })

      // fetchSubcontractorDetails
      .addCase(fetchSubcontractorDetails.pending, (state) => {
        state.subcontractorDetailsFetching = true
      })
      .addCase(fetchSubcontractorDetails.fulfilled, (state, action) => {
        state.subcontractorDetailsFetching = false
        state.subcontractorDetails = action.payload
      })
      .addCase(fetchSubcontractorDetails.rejected, (state) => {
        state.subcontractorDetailsFetching = false
      })

      // suspendSubcontractor
      .addCase(suspendSubcontractor.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(suspendSubcontractor.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = {
          type: RESULT_TYPES.SUSPEND,
          message: getResponseMessage(
            action.payload,
            "Subcontractor suspended successfully"
          )
        }
      })
      .addCase(suspendSubcontractor.rejected, (state) => {
        state.saveFetching = false
      })

      // activateSubcontractor
      .addCase(activateSubcontractor.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(activateSubcontractor.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = {
          type: RESULT_TYPES.UNSUSPEND,
          message: getResponseMessage(
            action.payload,
            "Subcontractor activated successfully"
          )
        }
      })
      .addCase(activateSubcontractor.rejected, (state) => {
        state.saveFetching = false
      })

      // archiveSubcontractor / restoreSubcontractor
      //
      // These set `saveResult` for the same reason every other mutation does:
      // it is what the page watches to refetch the list. Without it the row
      // stays on screen behind a success toast until something else reloads
      // the page — which is exactly how archiving shipped and was caught.
      .addCase(archiveSubcontractor.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(archiveSubcontractor.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = {
          type: RESULT_TYPES.ARCHIVE,
          message: getResponseMessage(action.payload, "Subcontractor archived.")
        }
      })
      .addCase(archiveSubcontractor.rejected, (state) => {
        state.saveFetching = false
      })

      .addCase(restoreSubcontractor.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(restoreSubcontractor.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = {
          type: RESULT_TYPES.RESTORE,
          message: getResponseMessage(action.payload, "Subcontractor restored.")
        }
      })
      .addCase(restoreSubcontractor.rejected, (state) => {
        state.saveFetching = false
      })

      // rateSubcontractor
      .addCase(rateSubcontractor.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(rateSubcontractor.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = {
          type: RESULT_TYPES.RATE,
          message: getResponseMessage(
            action.payload,
            "Subcontractor rated successfully"
          )
        }
      })
      .addCase(rateSubcontractor.rejected, (state) => {
        state.saveFetching = false
      })

      // resendInviteSubcontractor
      .addCase(resendInviteSubcontractor.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(resendInviteSubcontractor.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = {
          type: RESULT_TYPES.INVITE,
          message: getResponseMessage(
            action.payload,
            "Invite sent successfully"
          )
        }
      })
      .addCase(resendInviteSubcontractor.rejected, (state) => {
        state.saveFetching = false
      })

      // inviteNoAccessSubcontractor
      .addCase(inviteNoAccessSubcontractor.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(inviteNoAccessSubcontractor.fulfilled, (state, action) => {
        state.saveFetching = false
        state.saveResult = {
          type: RESULT_TYPES.INVITE,
          message: getResponseMessage(
            action.payload,
            "Invite sent successfully"
          )
        }
      })
      .addCase(inviteNoAccessSubcontractor.rejected, (state) => {
        state.saveFetching = false
      })

      .addCase(logout.fulfilled, () => {
        return initialState
      })
  }
})

export const {
  subcontractorsSetProp,
  subcontractorsSetFilters,
  subcontractorsClearFilters
} = subcontractorsSlice.actions

export default subcontractorsSlice
